import React, { useState, useEffect, useRef, useCallback, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { MagnifyingGlassIcon, UsersIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import { searchAPI, playersAPI, eventsAPI } from '../../services/api';
import type { SearchResult } from '../../types/search';
import { useGlobalModal } from '../../contexts/GlobalModalContext';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose }) => {
  const { openEventForm, openPlayerForm } = useGlobalModal();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const performSearch = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await searchAPI.search(searchQuery);
      setResults(response.results);
    } catch (error) {
      console.error('Search failed:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, performSearch]);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setResults([]);
      setLoading(false);
    }
  }, [isOpen]);

  const handleResultClick = async (result: SearchResult) => {
    onClose();
    try {
      if (result.type === 'player') {
        const player = await playersAPI.getById(result.id);
        openPlayerForm(player);
      } else if (result.type === 'event') {
        const event = await eventsAPI.getById(result.id);
        openEventForm(event);
      }
    } catch {
      // Fallback: if fetch fails, just close
    }
  };

  const getIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'player':
        return UsersIcon;
      case 'event':
        return CalendarDaysIcon;
      default:
        return MagnifyingGlassIcon;
    }
  };

  const getTypeLabel = (type: SearchResult['type']) => {
    switch (type) {
      case 'player':
        return 'Játékosok';
      case 'event':
        return 'Események';
      default:
        return type;
    }
  };

  const groupedResults = results.reduce<Record<string, SearchResult[]>>((acc, result) => {
    if (!acc[result.type]) {
      acc[result.type] = [];
    }
    acc[result.type].push(result);
    return acc;
  }, {});

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose} initialFocus={inputRef}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto p-4 sm:p-6 md:p-20">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="mx-auto max-w-xl transform overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black/5 transition-all">
              {/* Search input */}
              <div className="relative">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-3.5 h-5 w-5 text-gray-400" />
                <input
                  ref={inputRef}
                  type="text"
                  className="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm"
                  placeholder="Keresés..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>

              {/* Results area */}
              {query.length < 2 && (
                <div className="border-t border-gray-100 px-6 py-14 text-center text-sm text-gray-500">
                  <MagnifyingGlassIcon className="mx-auto h-6 w-6 text-gray-400" />
                  <p className="mt-2">Kezdj el gépelni a kereséshez...</p>
                  <p className="mt-1 text-xs text-gray-400">Minimum 2 karakter</p>
                </div>
              )}

              {query.length >= 2 && loading && (
                <div className="border-t border-gray-100 px-6 py-14 text-center text-sm text-gray-500">
                  <div className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-primary-600" />
                  <p className="mt-2">Keresés...</p>
                </div>
              )}

              {query.length >= 2 && !loading && results.length === 0 && (
                <div className="border-t border-gray-100 px-6 py-14 text-center text-sm text-gray-500">
                  <MagnifyingGlassIcon className="mx-auto h-6 w-6 text-gray-400" />
                  <p className="mt-2">Nincs találat</p>
                </div>
              )}

              {query.length >= 2 && !loading && results.length > 0 && (
                <div className="max-h-80 scroll-py-2 overflow-y-auto border-t border-gray-100">
                  {Object.entries(groupedResults).map(([type, items]) => {
                    const Icon = getIcon(type as SearchResult['type']);
                    return (
                      <div key={type}>
                        <h3 className="bg-gray-50 px-4 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          {getTypeLabel(type as SearchResult['type'])}
                        </h3>
                        <ul className="divide-y divide-gray-100">
                          {items.map((result) => (
                            <li key={`${result.type}-${result.id}`}>
                              <button
                                onClick={() => handleResultClick(result)}
                                className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                              >
                                <Icon className="h-5 w-5 flex-shrink-0 text-gray-400" />
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {result.title}
                                  </p>
                                  {result.subtitle && (
                                    <p className="text-xs text-gray-500 truncate">
                                      {result.subtitle}
                                    </p>
                                  )}
                                </div>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Footer hint */}
              <div className="border-t border-gray-100 bg-gray-50 px-4 py-2.5">
                <div className="flex items-center justify-end gap-2 text-xs text-gray-400">
                  <kbd className="rounded border border-gray-300 bg-white px-1.5 py-0.5 font-mono text-[10px]">ESC</kbd>
                  <span>bezárás</span>
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default SearchModal;
